import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollableTable, Column, SortConfig } from "@/components/ui/scrollable-table";
import { Pencil, Trash2 } from "lucide-react";
import { useState, useCallback, useMemo } from "react";

import { useCollection } from "@/hooks/firestore";
import { 
  useAddDocument, 
  useUpdateDocument, 
  // useDeleteDocument // No longer used directly
} from "@/hooks/firestore/operations";
import { usePermissions } from "@/hooks/usePermissions";
import { 
  Drink, 
  Brand, 
  Flavor, 
  Attribute,
  DrinkFlavor,
  DrinkAttribute
} from "@/types/firestore";
import { orderBy } from "firebase/firestore";

// Redux imports for deletion dialog
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { DeleteDialog } from "../editor/dialogs/DeleteConfirmDialog";
import { 
    EditableEntityType,
    startDeletionAssessment, 
    assessDeletionImpact, 
    deleteEntity, 
    cancelInteraction,
    selectEditorInteractionState,
    selectSelectedEntity,
    selectDeletionImpact,
    selectEditorOperationLoading,
    selectEditorOperationError
} from "@/store/slices/editorSlice";

export const DrinkManager = () => {
  const dispatch = useAppDispatch();
  const { documents: brands, loading: brandsLoading } = useCollection<Brand>('brands', [orderBy('name')]);
  const { documents: flavors, loading: flavorsLoading } = useCollection<Flavor>('flavors', [orderBy('name')]);
  const { documents: attributes, loading: attributesLoading } = useCollection<Attribute>('attributes', [orderBy('name')]);
  const { documents: drinks, loading: drinksCollectionLoading } = useCollection<Drink>('drinks');
  
  // Instantiate hooks for drinks
  const { addDocument: addDrink, loading: addingDrink, error: addDrinkError } = useAddDocument<Drink>('drinks');
  const { updateDocument: updateDrinkHook, loading: updatingDrink, error: updateDrinkError } = useUpdateDocument<Drink>('drinks'); // Renamed to avoid conflict with editorSlice thunk
  // const { deleteDocument: deleteDrink, loading: deletingDrink, error: deleteDrinkError } = useDeleteDocument('drinks'); // Replaced

  // Editor slice selectors
  const interactionState = useAppSelector(selectEditorInteractionState);
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const deletionImpact = useAppSelector(selectDeletionImpact);
  const operationLoading = useAppSelector(selectEditorOperationLoading);
  const operationError = useAppSelector(selectEditorOperationError);
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingDrink, setEditingDrink] = useState<Drink | null>(null);
  const [filterBrandSize, setFilterBrandSize] = useState<string | null>(null);
  const [filterFlavorId, setFilterFlavorId] = useState<string | null>(null);
  const [filterAttributeId, setFilterAttributeId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [newDrink, setNewDrink] = useState({ 
    name: "", 
    brandId: "", 
    brandName: "",
    flavors: [] as DrinkFlavor[], 
    attributes: [] as DrinkAttribute[] 
  });

  const { hasPermission } = usePermissions();
  const canManage = hasPermission('editor') || hasPermission('admin');

  // Helper function to get flavor/attribute names for display and sorting
  const getFlavorNames = useCallback((drinkFlavors: DrinkFlavor[] | undefined | null) => {
    return drinkFlavors?.map(flavor => flavor.name).join(", ") || "";
  }, []);

  const getAttributeNames = useCallback((drinkAttributes: DrinkAttribute[] | undefined | null) => {
    return drinkAttributes?.map(attribute => attribute.name).join(", ") || "";
  }, []);

  // Filter drinks - sorting is now handled by ScrollableTable
  const processedDrinks = useMemo(() => {
    if (!drinks) return [];
    let filtered = drinks;

    // Filter by flavor
    if (filterFlavorId) {
      filtered = filtered.filter(drink => {
        if (!Array.isArray(drink.flavors) || drink.flavors.length === 0) return false;
        return drink.flavors.some(f => f.id === filterFlavorId || f.id.trim() === filterFlavorId.trim() || f.id.toLowerCase() === filterFlavorId.toLowerCase());
      });
    }
    
    // Filter by attribute
    if (filterAttributeId) {
      filtered = filtered.filter(drink => {
        if (!Array.isArray(drink.attributes) || drink.attributes.length === 0) return false;
        return drink.attributes.some(a => a?.id === filterAttributeId || a?.id?.trim() === filterAttributeId.trim() || a?.id?.toLowerCase() === filterAttributeId.toLowerCase());
      });
    }
    
    // Filter by brand size
    if (filterBrandSize !== null) {
      filtered = filtered.filter(drink => {
        if (!drink.brandId) return false;
        const foundBrand = brands?.find(b => b.id === drink.brandId);
        return foundBrand && foundBrand.brandSize === filterBrandSize;
      });
    }

    // Filter by search term (name or brandName)
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(drink => 
        drink.name.toLowerCase().includes(lowerSearchTerm) ||
        (drink.brandName && drink.brandName.toLowerCase().includes(lowerSearchTerm))
      );
    }

    return filtered;
  }, [drinks, brands, filterFlavorId, filterAttributeId, filterBrandSize, searchTerm, getFlavorNames, getAttributeNames]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleAddDrink = useCallback(async (): Promise<void> => {
    if (!newDrink.brandId) {
      toast.error("Please select a brand");
      return;
    }

    // Find the brand name for denormalization
    const brand = brands?.find(b => b.id === newDrink.brandId);
    if (!brand) {
      toast.error("Selected brand not found");
      return;
    }
    if (!newDrink.name.trim()) {
      toast.error("Drink name cannot be empty");
      return;
    }

    try {
      const drinkToAdd: Omit<Drink, 'id' | 'createdAt'> = {
        name: newDrink.name.trim(),
        brandId: newDrink.brandId,
        brandName: brand.name, // Use fetched brand name
        brandSize: brand.brandSize, // Use fetched brand size
        flavors: newDrink.flavors,
        attributes: newDrink.attributes,
        // Default computed fields
        averageRating: 0,
        reviewCount: 0, 
        publicReviewCount: 0,
        sightingCount: 0,
        ratingSum: 0 // Initialize ratingSum
      };
      
      const newId = await addDrink(drinkToAdd);

      if (newId) {
        setNewDrink({ 
          name: "", 
          brandId: "", 
          brandName: "",
          flavors: [], 
          attributes: [] 
        });
        setIsAddDialogOpen(false);
        toast.success(`Drink "${drinkToAdd.name}" added.`);
      } else {
        toast.error(`Failed to add drink: ${addDrinkError?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error adding drink:", error);
      toast.error("Failed to add drink. See console for details.");
    }
  }, [addDrink, newDrink, brands, addDrinkError]);

  const handleSaveEdit = useCallback(async (updatedDrink: Drink): Promise<void> => {
    if (!updatedDrink.id) {
      toast.error("Cannot save drink without an ID.");
      return;
    }
    if (!updatedDrink.name.trim()) {
      toast.error("Drink name cannot be empty.");
      return;
    }
     if (!updatedDrink.brandId) {
      toast.error("Brand cannot be empty.");
      return;
    }
    // Find brand name again in case brandId was changed
     const brand = brands?.find(b => b.id === updatedDrink.brandId);
     if (!brand) {
       toast.error("Selected brand not found");
       return;
     }

    try {
      const success = await updateDrinkHook(updatedDrink.id, {
        name: updatedDrink.name.trim(),
        brandId: updatedDrink.brandId,
        brandName: brand.name, // Ensure brandName is updated if brandId changes
        flavors: updatedDrink.flavors,
        attributes: updatedDrink.attributes
        // Note: Computed fields (rating, counts) are NOT updated here, they are handled by backend functions
      });

      if (success) {
        setEditingDrink(null);
        toast.success(`Drink "${updatedDrink.name}" updated.`);
      } else {
        toast.error(`Failed to update drink: ${updateDrinkError?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error updating drink:", error);
      toast.error("Failed to update drink. See console for details.");
    }
  }, [updateDrinkHook, brands, updateDrinkError]);

  const handleCancelEdit = useCallback(() => {
    setEditingDrink(null);
  }, []);

  const handleDeleteDrinkAction = useCallback((drink: Drink) => {
    if (!drink || !drink.id) return;
    dispatch(startDeletionAssessment({ entityType: 'drink', entityId: drink.id }));
    dispatch(assessDeletionImpact({ entityType: 'drink', entityId: drink.id }));
  }, [dispatch]);

  const handleDeletionDialogClose = useCallback(() => {
    dispatch(cancelInteraction());
  }, [dispatch]);

  const handleConfirmDrinkDeletion = useCallback(async () => {
    if (selectedEntity && selectedEntity.type === 'drink' && selectedEntity.id) {
      try {
        await dispatch(deleteEntity({ entityType: 'drink', entityId: selectedEntity.id })).unwrap();
        
        if (editingDrink?.id === selectedEntity.id) {
          setEditingDrink(null);
        }
      } catch (error: any) {
        // Error handling is now done by the dialog component
        throw error;
      }
    }
  }, [dispatch, selectedEntity, editingDrink]);

  const getDeletionAssessmentState = useCallback(() => {
    if (deletionImpact.error) return 'error';
    if (deletionImpact.loading === 'pending') return 'loading';
    return 'completed';
  }, [deletionImpact.loading, deletionImpact.error]);

  const getBrandName = (id: string | undefined) => {
    if (!id) return "Unknown";
    const foundBrand = (brands || []).find(b => b.id === id);
    return foundBrand ? foundBrand.name : "Unknown";
  };

  // Helper to add a flavor to the new drink
  const addFlavorToDrink = (flavorId: string) => {
    const flavorToAdd = (flavors || []).find(f => f.id === flavorId);
    if (!flavorToAdd || !flavorToAdd.id) return;
    
    // Check if already added
    if (newDrink.flavors.some((f: DrinkFlavor) => f.id === flavorId)) return;
    
    setNewDrink({
      ...newDrink,
      flavors: [...newDrink.flavors, { 
        id: flavorToAdd.id, 
        name: flavorToAdd.name,
        groupId: flavorToAdd.groupId 
      }]
    });
  };

  // Helper to add an attribute to the new drink
  const addAttributeToDrink = (attributeId: string) => {
    const attributeToAdd = (attributes || []).find(a => a.id === attributeId);
    if (!attributeToAdd || !attributeToAdd.id) return;
    
    // Check if already added
    if (newDrink.attributes.some((a: DrinkAttribute) => a.id === attributeId)) return;
    
    setNewDrink({
      ...newDrink,
      attributes: [...newDrink.attributes, { 
        id: attributeToAdd.id, 
        name: attributeToAdd.name,
        groupId: attributeToAdd.groupId 
      }]
    });
  };

  // Helper to remove a flavor from the new drink
  const removeFlavorFromDrink = (flavorId: string) => {
    setNewDrink({
      ...newDrink,
      flavors: newDrink.flavors.filter((f: DrinkFlavor) => f.id !== flavorId)
    });
  };

  // Helper to remove an attribute from the new drink
  const removeAttributeFromDrink = (attributeId: string) => {
    setNewDrink({
      ...newDrink,
      attributes: newDrink.attributes.filter((a: DrinkAttribute) => a.id !== attributeId)
    });
  };

  const loading = brandsLoading || flavorsLoading || attributesLoading || drinksCollectionLoading || addingDrink || updatingDrink || (operationLoading === 'pending' && interactionState === 'confirming_deletion');

  // Update brand name when brand ID changes
  const handleBrandChange = (brandId: string) => {
    const selectedBrand = brands?.find(b => b.id === brandId);
    setNewDrink({
      ...newDrink, 
      brandId,
      brandName: selectedBrand?.name || ""
    });
  };

  // Helper to add a flavor to the editing drink
  const addFlavorToEditingDrink = (flavorId: string) => {
    const flavorToAdd = (flavors || []).find(f => f.id === flavorId);
    if (!flavorToAdd || !flavorToAdd.id || !editingDrink) return;
    
    // Check if already added
    if (editingDrink.flavors.some((f: DrinkFlavor) => f.id === flavorId)) return;
    
    setEditingDrink({
      ...editingDrink,
      flavors: [...editingDrink.flavors, { 
        id: flavorToAdd.id, 
        name: flavorToAdd.name,
        groupId: flavorToAdd.groupId 
      }]
    });
  };

  // Helper to add an attribute to the editing drink
  const addAttributeToEditingDrink = (attributeId: string) => {
    const attributeToAdd = (attributes || []).find(a => a.id === attributeId);
    if (!attributeToAdd || !attributeToAdd.id || !editingDrink) return;
    
    // Check if already added
    if (editingDrink.attributes.some((a: DrinkAttribute) => a.id === attributeId)) return;
    
    setEditingDrink({
      ...editingDrink,
      attributes: [...editingDrink.attributes, { 
        id: attributeToAdd.id, 
        name: attributeToAdd.name,
        groupId: attributeToAdd.groupId 
      }]
    });
  };

  // Helper to remove a flavor from the editing drink
  const removeFlavorFromEditingDrink = (flavorId: string) => {
    if (!editingDrink) return;
    setEditingDrink({
      ...editingDrink,
      flavors: editingDrink.flavors.filter((f: DrinkFlavor) => f.id !== flavorId)
    });
  };

  // Helper to remove an attribute from the editing drink
  const removeAttributeFromEditingDrink = (attributeId: string) => {
    if (!editingDrink) return;
    setEditingDrink({
      ...editingDrink,
      attributes: editingDrink.attributes.filter((a: DrinkAttribute) => a.id !== attributeId)
    });
  };

  // Create table columns configuration
  const tableColumns = useMemo(() => {
    const columns: Column<Drink>[] = [];
    
    columns.push({
      id: 'brandName',
      header: 'Brand',
      cell: (drink) => editingDrink?.id === drink.id ? (
        <Select
          value={editingDrink.brandId}
          onValueChange={(value) => {
            const selectedBrand = brands?.find(b => b.id === value);
            setEditingDrink({
              ...editingDrink,
              brandId: value,
              brandName: selectedBrand?.name || editingDrink.brandName,
              brandSize: selectedBrand?.brandSize || editingDrink.brandSize
            });
          }}
          disabled={!canManage}
        >
          <SelectTrigger className="w-full">
            <SelectValue>{editingDrink.brandName || getBrandName(editingDrink.brandId)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {brands?.map(brand => (
              brand.id && (
                <SelectItem key={brand.id} value={brand.id}>
                  {brand.name} ({brand.brandSize === "craft" ? "Craft" : brand.brandSize === "mass" ? "Commercial" : "Unknown"})
                </SelectItem>
              )
            ))}
          </SelectContent>
        </Select>
      ) : (
        drink.brandName || getBrandName(drink.brandId)
      ),
      sortable: true,
      minWidth: '200px'
    });

    columns.push({
      id: 'name',
      header: 'Name',
      cell: (drink) => editingDrink?.id === drink.id ? (
        <Input
          value={editingDrink.name}
          onChange={(e) => setEditingDrink({...editingDrink, name: e.target.value})}
          disabled={!canManage}
        />
      ) : drink.name,
      sortable: true,
      minWidth: '200px'
    });

    columns.push({
      id: 'flavors',
      header: 'Flavors',
      cell: (drink) => editingDrink?.id === drink.id ? (
        <div className="space-y-2">
          <Select
            value="multiple"
            onValueChange={(value) => {
              if (value !== "multiple") {
                addFlavorToEditingDrink(value);
              }
            }}
            disabled={!canManage}
          >
            <SelectTrigger className="w-full">
              <SelectValue>
                {editingDrink.flavors.length === 0
                  ? "Select flavors"
                  : `${editingDrink.flavors.length} flavor(s) selected`}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="multiple" disabled>Select multiple flavors</SelectItem>
              {flavors?.map(flv => (
                flv.id && (
                  <SelectItem key={flv.id} value={flv.id}>
                    {flv.name}
                  </SelectItem>
                )
              ))}
            </SelectContent>
          </Select>
          {editingDrink.flavors.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {editingDrink.flavors.map(flavor => (
                <div key={flavor.id} className="bg-muted rounded-md px-2 py-1 flex items-center gap-1 text-xs">
                  <span>{flavor.name}</span>
                  {canManage && (
                    <button 
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => removeFlavorFromEditingDrink(flavor.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : getFlavorNames(drink.flavors),
      sortable: true,
      minWidth: '250px'
    });

    columns.push({
      id: 'attributes',
      header: 'Attributes',
      cell: (drink) => editingDrink?.id === drink.id ? (
        <div className="space-y-2">
          <Select
            value="multiple"
            onValueChange={(value) => {
              if (value !== "multiple") {
                addAttributeToEditingDrink(value);
              }
            }}
            disabled={!canManage}
          >
            <SelectTrigger className="w-full">
              <SelectValue>
                {editingDrink.attributes.length === 0
                  ? "Select attributes"
                  : `${editingDrink.attributes.length} attribute(s) selected`}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="multiple" disabled>Select multiple attributes</SelectItem>
              {attributes?.map(attr => (
                attr.id && (
                  <SelectItem key={attr.id} value={attr.id}>
                    {attr.name}
                  </SelectItem>
                )
              ))}
            </SelectContent>
          </Select>
          {editingDrink.attributes.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {editingDrink.attributes.map(attribute => (
                <div key={attribute.id} className="bg-muted rounded-md px-2 py-1 flex items-center gap-1 text-xs">
                  <span>{attribute.name}</span>
                  {canManage && (
                    <button 
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => removeAttributeFromEditingDrink(attribute.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : getAttributeNames(drink.attributes),
      sortable: true,
      minWidth: '250px'
    });

    if (canManage) {
      columns.push({
        id: 'actions',
        header: 'Actions',
        cell: (drink) => editingDrink?.id === drink.id ? (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleSaveEdit(editingDrink)} disabled={!canManage}>Save</Button>
            <Button variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingDrink(drink)}
              title="Edit drink"
              disabled={!canManage}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleDeleteDrinkAction(drink)}
              title="Delete drink"
              disabled={!drink.id || !canManage}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
        sortable: false,
        minWidth: '180px'
      });
    }
    
    return columns;
  }, [editingDrink, canManage, brands, flavors, attributes, getBrandName, getFlavorNames, getAttributeNames, handleDeleteDrinkAction, handleSaveEdit, handleCancelEdit, addFlavorToEditingDrink, addAttributeToEditingDrink, removeFlavorFromEditingDrink, removeAttributeFromEditingDrink]);
  
  // Create sort config for the table
  const tableSortConfig: SortConfig<Drink> = useMemo(() => ({
    column: sortColumn as keyof Drink | null,
    direction: sortDirection
  }), [sortColumn, sortDirection]);
  
  // Empty message with filters applied
  const emptyMessage = useMemo(() => {
    let message = "No drinks found";
    if (searchTerm) message += ` for "${searchTerm}"`;
    if (filterBrandSize !== null) message += ` of brand type "${filterBrandSize}"`;
    if (filterFlavorId !== null) {
      const flavorName = flavors?.find(f => f.id === filterFlavorId)?.name;
      if (flavorName) message += ` with flavor "${flavorName}"`;
    }
    if (filterAttributeId !== null) {
      const attributeName = attributes?.find(a => a.id === filterAttributeId)?.name;
      if (attributeName) message += ` with attribute "${attributeName}"`;
    }
    return message + '.';
  }, [searchTerm, filterBrandSize, filterFlavorId, filterAttributeId, flavors, attributes]);

  return (
    <>
      <Card className="h-full flex flex-col overflow-hidden">
        <CardHeader>
          <CardTitle>Soda Drinks ({drinks?.length || 0})</CardTitle>
          <CardDescription>Manage your soda drinks here</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col overflow-hidden pb-6">
          <div className="flex justify-between items-start mb-4 gap-4">
            <div className="flex gap-2 items-end flex-wrap">
              <div>
                <Label htmlFor="brandFilter">Filter by Brand Type</Label>
                <Select 
                  value={filterBrandSize === null ? "all" : filterBrandSize} 
                  onValueChange={(value) => {
                    setFilterBrandSize(value === "all" ? null : value);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="craft">Craft</SelectItem>
                    <SelectItem value="mass">Commercial</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="flavorFilter">Filter by Flavor</Label>
                <Select 
                  value={filterFlavorId || "all"} 
                  onValueChange={(value) => {
                    setFilterFlavorId(value === "all" ? null : value);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select flavor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Flavors</SelectItem>
                    {flavors?.map(flv => (
                      flv.id && (
                        <SelectItem key={flv.id} value={flv.id}>
                          {flv.name}
                        </SelectItem>
                      )
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="attributeFilter">Filter by Attribute</Label>
                <Select 
                  value={filterAttributeId || "all"} 
                  onValueChange={(value) => {
                    setFilterAttributeId(value === "all" ? null : value);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select attribute" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Attributes</SelectItem>
                    {attributes?.map(attr => (
                      attr.id && (
                        <SelectItem key={attr.id} value={attr.id}>
                          {attr.name}
                        </SelectItem>
                      )
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="searchTerm">Filter by Name/Brand</Label>
                <Input 
                  id="searchTerm"
                  placeholder="Search name or brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-[200px]"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              {canManage && (
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button disabled={!canManage}>Add New Drink</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Drink</DialogTitle>
                      <DialogDescription>
                        Fill in the details for the new soda drink.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="name" className="text-right">Name</Label>
                        <Input 
                          id="name" 
                          value={newDrink.name} 
                          onChange={(e) => setNewDrink({...newDrink, name: e.target.value})}
                          className="col-span-3"
                        />
                      </div>
                      
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="brand" className="text-right">Brand</Label>
                        <Select
                          value={newDrink.brandId}
                          onValueChange={handleBrandChange}
                        >
                          <SelectTrigger className="col-span-3">
                            <SelectValue placeholder="Select brand" />
                          </SelectTrigger>
                          <SelectContent>
                            {brands
                              ?.filter(b => b.id)
                              .map(b => (
                              <SelectItem key={b.id} value={b.id!}>
                                {b.name} ({b.brandSize === "craft" ? "Craft" : b.brandSize === "mass" ? "Commercial" : "Unknown"})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="flavors" className="text-right">Flavors</Label>
                        <Select
                          value="multiple"
                          onValueChange={(value) => {
                            if (value !== "multiple") {
                              addFlavorToDrink(value);
                            }
                          }}
                        >
                          <SelectTrigger className="col-span-3">
                            <SelectValue>
                              {newDrink.flavors.length === 0 
                                ? "Select flavors" 
                                : `${newDrink.flavors.length} flavor(s) selected`}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="multiple" disabled>Select multiple flavors</SelectItem>
                            {flavors?.map(flv => (
                              flv.id && (
                                <SelectItem key={flv.id} value={flv.id}>
                                  {flv.name}
                                </SelectItem>
                              )
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {newDrink.flavors.length > 0 && (
                        <div className="grid grid-cols-4 items-center gap-4">
                          <div></div>
                          <div className="col-span-3 flex flex-wrap gap-1">
                            {newDrink.flavors.map(flavor => (
                              <div key={flavor.id} className="bg-muted rounded-md px-2 py-1 flex items-center gap-1">
                                <span>{flavor.name}</span>
                                <button 
                                  className="text-xs text-muted-foreground hover:text-foreground"
                                  onClick={() => removeFlavorFromDrink(flavor.id)}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="attributes" className="text-right">Attributes</Label>
                        <Select
                          value="multiple"
                          onValueChange={(value) => {
                            if (value !== "multiple") {
                              addAttributeToDrink(value);
                            }
                          }}
                        >
                          <SelectTrigger className="col-span-3">
                            <SelectValue>
                              {newDrink.attributes.length === 0 
                                ? "Select attributes" 
                                : `${newDrink.attributes.length} attribute(s) selected`}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="multiple" disabled>Select multiple attributes</SelectItem>
                            {attributes?.map(attr => (
                              attr.id && (
                                <SelectItem key={attr.id} value={attr.id}>
                                  {attr.name}
                                </SelectItem>
                              )
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {newDrink.attributes.length > 0 && (
                        <div className="grid grid-cols-4 items-center gap-4">
                          <div></div>
                          <div className="col-span-3 flex flex-wrap gap-1">
                            {newDrink.attributes.map(attribute => (
                              <div key={attribute.id} className="bg-muted rounded-md px-2 py-1 flex items-center gap-1">
                                <span>{attribute.name}</span>
                                <button 
                                  className="text-xs text-muted-foreground hover:text-foreground"
                                  onClick={() => removeAttributeFromDrink(attribute.id)}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <DialogFooter>
                      <Button onClick={handleAddDrink} disabled={!canManage}>Add Drink</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="flex-1 flex items-center justify-center">Loading drinks data...</div>
          ) : (
            <ScrollableTable
              data={processedDrinks}
              columns={tableColumns}
              sortConfig={tableSortConfig}
              onSort={handleSort}
              emptyMessage={emptyMessage}
              height="calc(100vh - 370px)"
            />
          )}
        </CardContent>
      </Card>

      {/* Deletion Dialog */}
      {(interactionState === 'confirming_deletion' || interactionState === 'assessing_deletion') && selectedEntity?.type === 'drink' && (
        <DeleteDialog
          isOpen={true}
          onClose={handleDeletionDialogClose}
          entityType={selectedEntity.type as EditableEntityType}
          entityName={deletionImpact.report?.primaryDocument.name || selectedEntity.id || 'Drink'}
          assessmentState={getDeletionAssessmentState()}
          impactReport={deletionImpact.report}
          deletionState={operationLoading === 'pending' ? 'loading' : operationError ? 'error' : 'idle'}
          error={deletionImpact.error || operationError}
          onConfirm={handleConfirmDrinkDeletion}
        />
      )}
    </>
  );
}; 